import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { UserRepository } from '@big-direkt/state/user';
import { FormFieldUpdate } from '../../interfaces/form-field-update';
import { type WebformSubmissionResponse } from '../../interfaces/webform-submission-response';
import { type WebformSubmissionDataResponse } from '../../interfaces/webform-submission-response-data';
import { FormStateService } from '../form-state/form-state.service';
import { FormUpdateService } from '../form-update/form-update.service';
import { SubmissionResponseType } from './submission-response-type';

interface CategorizedResponseData {
    pdfDownloadItems: WebformSubmissionDataResponse[];
    formUpdateItem?: WebformSubmissionDataResponse;
    verifyUserDataItem?: WebformSubmissionDataResponse;
    loginItem?: WebformSubmissionDataResponse;
    redirectItem?: WebformSubmissionDataResponse;
}

@Injectable({
    providedIn: 'any',
})
export class SubmissionResponseHandlerService {
    private readonly formStateService = inject(FormStateService);
    private readonly formUpdateService = inject(FormUpdateService);
    private readonly router = inject(Router);
    private readonly userRepository = inject(UserRepository);

    public handle(response: WebformSubmissionResponse): void {
        const data = this.normalizeResponseData(response.data);
        const categorizedData = this.categorizeResponseData(data);

        this.processPdfDownloads(categorizedData.pdfDownloadItems, response);
        this.processFormUpdate(categorizedData.formUpdateItem);
        this.processVerifyUserData(categorizedData.verifyUserDataItem);
        this.processLogin(categorizedData.loginItem);
        this.processRedirect(categorizedData.redirectItem);
    }

    private normalizeResponseData(responseData: WebformSubmissionResponse['data']): WebformSubmissionDataResponse[] {
        if (!responseData) {
            return [];
        }

        return Array.isArray(responseData) ? responseData : [responseData];
    }

    private categorizeResponseData(data: WebformSubmissionDataResponse[]): CategorizedResponseData {
        const result: CategorizedResponseData = {
            pdfDownloadItems: [],
            formUpdateItem: undefined,
            verifyUserDataItem: undefined,
            loginItem: undefined,
            redirectItem: undefined,
        };

        for (const item of data) {
            const { type } = item;

            if (!type) {
                continue;
            }

            switch (type) {
                case SubmissionResponseType.PdfDownload:
                    result.pdfDownloadItems.push(item);
                    break;

                case SubmissionResponseType.FormUpdate:
                    result.formUpdateItem ??= item;
                    break;

                case SubmissionResponseType.VerifyUserData:
                    result.verifyUserDataItem ??= item;
                    break;

                case SubmissionResponseType.Login:
                    result.loginItem ??= item;
                    break;

                case SubmissionResponseType.Redirect:
                    result.redirectItem ??= item;
                    break;

                default:
                    break;
            }
        }

        return result;
    }

    private processPdfDownloads(items: WebformSubmissionDataResponse[], response: WebformSubmissionResponse): void {
        if (items.length > 0) {
            this.formStateService.registerResponse(response);
        }
    }

    private processFormUpdate(item?: WebformSubmissionDataResponse): void {
        if (!item) {
            return;
        }

        const configuration = item.attributes?.configuration;
        let configurationArray: FormFieldUpdate[] = [];

        if (configuration === undefined) {
            configurationArray = [];
        } else if (!Array.isArray(configuration)) {
            configurationArray.push(configuration);
        } else {
            configurationArray = configuration;
        }

        if (configurationArray.length > 0) {
            this.formUpdateService.updateFormFields(configurationArray);
        }
    }

    private processVerifyUserData(item?: WebformSubmissionDataResponse): void {
        if (!item) {
            return;
        }

        const configuration = item.attributes?.configuration;
        const token = item.attributes?.token;

        let configurationArray: FormFieldUpdate[] = [];

        if (configuration === undefined) {
            configurationArray = [];
        } else if (!Array.isArray(configuration)) {
            configurationArray.push(configuration);
        } else {
            configurationArray = configuration;
        }

        if (configurationArray.length > 0) {
            this.formUpdateService.updateFormFields(configurationArray);
        }

        if (token) {
            this.userRepository.setToken(token);
        }
    }

    private processLogin(item?: WebformSubmissionDataResponse): void {
        if (!item) {
            return;
        }

        const token = item.attributes?.token;

        if (token) {
            this.userRepository.setToken(token);
        }

        this.formStateService.handleDestinationRedirection();
    }

    private processRedirect(item?: WebformSubmissionDataResponse): void {
        if (!item) {
            return;
        }

        const url = item.attributes?.url;

        if (url) {
            void this.router.navigateByUrl(url);
        }
    }
}
